import React, { useState, useEffect } from 'react';
import Hero from '../../components/public/Hero';
import {
    FaShieldAlt, FaHeartbeat, FaCar, FaPlane, FaUmbrella,
    FaPiggyBank, FaGraduationCap, FaBriefcase, FaChartLine,
    FaFileContract, FaBuilding, FaQuestionCircle, FaPhoneAlt,
    FaLifeRing, FaStar
} from 'react-icons/fa';
import leadService from '../../services/lead.service';
import { useAuth } from '../../hooks/useAuth';
import { getLatestReportByEmail } from '../../services/diagnosisReports.service';

const Insurance = () => {
    const { user, isAuthenticated } = useAuth();
    const [selectedInsurance, setSelectedInsurance] = useState(null);
    const [activeFilter, setActiveFilter] = useState('All');
    const [isContactModalOpen, setContactModalOpen] = useState(false);
    const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [diagnosisReport, setDiagnosisReport] = useState(null);
    const [recommendedTypes, setRecommendedTypes] = useState([]);

    useEffect(() => {
        const fetchReport = async () => {
            if (isAuthenticated && user?.email) {
                try {
                    const report = await getLatestReportByEmail(
                        user.email,
                        user.id || user.uid
                    );
                    if (report) {
                        setDiagnosisReport(report);
                        determineRecommendations(report);
                    }
                } catch (error) {
                    console.error("Error fetching report for recommendations:", error);
                }
            }
        };
        fetchReport();
    }, [isAuthenticated, user]);

    const determineRecommendations = (report) => {
        const recommended = [];
        const { personalInfo, salaryIncome, expenses, salary } = report;

        // 1. Term Insurance Logic
        // If income is high but life insurance premium is low/missing
        const lifePremium = parseFloat(salaryIncome?.lifeInsurance) || 0;
        if (lifePremium < 1000) {
            recommended.push('Term Insurance');
            recommended.push('Life Insurance');
        }

        // 2. Health Insurance Logic
        const healthPremium = parseFloat(salaryIncome?.healthInsurance) || 0;
        if (healthPremium < 500) {
            recommended.push('Health Insurance');
        }

        // 3. Investment related
        if (expenses?.surplus > 0) {
            recommended.push('Equity');
            recommended.push('Bonds');
            recommended.push('Schema');
        }

        // 4. Business protection
        if (personalInfo?.incomeType === 'business' || personalInfo?.incomeType === 'business_partnership') {
            recommended.push('Business Insurance');
        }

        // 5. Retirement
        const ppf = parseFloat(salaryIncome?.ppf) || 0;
        const nps = parseFloat(salaryIncome?.nps_80ccd2) || 0;
        if (ppf + nps < 2000) {
            recommended.push('Retirement Plans');
        }

        setRecommendedTypes(recommended);
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const leadData = {
            ...contactForm,
            subject: `Insurance Inquiry: ${selectedInsurance?.title}`,
            message: `User is interested in ${selectedInsurance?.title} (${selectedInsurance?.category})`,
            type: 'insurance',
            insuranceType: selectedInsurance?.title
        };

        try {
            const response = await leadService.createLead(leadData);
            if (response.success) {
                alert(`Thank you ${contactForm.name}! Your inquiry for ${selectedInsurance?.title} has been sent. Our expert team will contact you shortly.`);
                setContactModalOpen(false);
                setSelectedInsurance(null); // Close both modals
                setContactForm({ name: '', phone: '', email: '' });
            } else {
                alert("Something went wrong. Please try again later.");
            }
        } catch (error) {
            console.error("Error submitting lead:", error);
            alert("Error submitting inquiry. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const categories = ['All', 'Protection', 'Health & Life', 'Assets', 'Investments'];

    const insuranceTypes = [
        {
            title: 'Term Insurance',
            category: 'Health & Life',
            icon: <FaShieldAlt />,
            description: 'Pure protection for your family at affordable premiums, ensuring financial security in your absence.',
            detailedInfo: 'Term Insurance is the simplest and most pure form of life insurance. It provides financial protection to your family for a specific "term" or period. If the insured person passes away during this term, the sum assured is paid to the beneficiaries. It offers high coverage at a very low cost, making it an essential part of any financial plan.',
            benefits: ['High coverage at low premiums', 'Financial security for dependents', 'Tax benefits under prevailing laws', 'Peace of mind'],
            color: 'light'
        },
        {
            title: 'Life Insurance',
            category: 'Health & Life',
            icon: <FaLifeRing />,
            description: 'Comprehensive life coverage tailored to your long-term financial goals and legacy planning.',
            detailedInfo: 'Life Insurance goes beyond just protection; it can also be a tool for long-term savings and wealth creation. Whether it\'s endowment plans, money-back policies, or whole-life insurance, these plans help you build a corpus while providing a safety net for your family. It ensures your financial legacy remains intact.',
            benefits: ['Wealth accumulation', 'Guaranteed returns (in some plans)', 'Coverage for entire life', 'Asset protection'],
            color: 'light'
        },
        {
            title: 'Health Insurance',
            category: 'Health & Life',
            icon: <FaHeartbeat />,
            description: 'Complete medical security for you and your family, covering hospitalization and critical care.',
            detailedInfo: 'Health Insurance protects you from the skyrocketing costs of medical treatment. It covers hospitalization expenses, pre and post-hospitalization costs, daycare procedures, and even critical illnesses. With rising medical inflation, having a robust health policy is crucial to prevent medical emergencies from draining your savings.',
            benefits: ['Cashless hospitalization', 'Coverage for critical illnesses', 'Preventive health check-ups', 'No-claim bonus benefits'],
            color: 'light'
        },
        {
            title: 'Motor Insurance',
            category: 'Assets',
            icon: <FaCar />,
            description: 'Stay protected on the road with comprehensive coverage for your vehicles against damage and liability.',
            detailedInfo: 'Motor Insurance is mandatory by law but also provides essential protection for your vehicle. Comprehensive policies cover damages to your vehicle due to accidents, theft, fire, and natural disasters, along with third-party liability. It ensures that road mishaps don\'t become major financial burdens.',
            benefits: ['Protection against theft and accidents', 'Third-party liability coverage', 'Personal accident cover', 'Roadside assistance'],
            color: 'light'
        },
        {
            title: 'Travel Insurance',
            category: 'Protection',
            icon: <FaPlane />,
            description: 'Worry-free journeys with coverage for medical emergencies, trip cancellations, and lost baggage.',
            detailedInfo: 'Travel Insurance is your safety net when traveling abroad or within the country. It covers unforeseen events like medical emergencies in a foreign land, trip cancellations or delays, and loss of passport or baggage. It allows you to explore the world with complete peace of mind.',
            benefits: ['Emergency medical evacuation', 'Compensation for lost luggage', 'Trip cancellation reimbursement', '24/7 global assistance'],
            color: 'light'
        },
        {
            title: 'General Insurance',
            category: 'Protection',
            icon: <FaUmbrella />,
            description: 'Broad protection for your valuables, home, and other miscellaneous assets against unforeseen risks.',
            detailedInfo: 'General Insurance covers non-life assets like your home, factory, or personal belongings. Home insurance, for example, protects your sanctuary and its contents from fire, theft, and natural calamities. It ensures that the physical assets you\'ve worked hard to build are well-protected.',
            benefits: ['Asset protection', 'Coverage for natural disasters', 'Theft and burglary protection', 'Liability coverage'],
            color: 'light'
        },
        {
            title: 'Retirement Plans',
            category: 'Investments',
            icon: <FaPiggyBank />,
            description: 'Secure your post-retirement life with structured accumulation and pension solutions.',
            detailedInfo: 'Retirement Plans help you build a financial cushion for your golden years. By investing systematically during your working life, you ensure a steady stream of income after you stop working. These plans help you maintain your lifestyle and independence in the later stages of life.',
            benefits: ['Regular pension income', 'Inflation-protected savings', 'Tax-deferred growth', 'Financial independence'],
            color: 'light'
        },
        {
            title: 'Child Education Plans',
            category: 'Investments',
            icon: <FaGraduationCap />,
            description: 'Invest today to ensure your child\'s educational dreams are never compromised by financial constraints.',
            detailedInfo: 'Child Education Plans are designed to provide funds for your child\'s future educational milestones. Even in your absence, these plans ensure that your child\'s education continues unhindered through waiver of premium benefits and periodic payouts. It\'s an investment in their future.',
            benefits: ['Funding higher education', 'Waiver of premium benefits', 'Flexible payout options', 'Guaranteed future for children'],
            color: 'light'
        },
        {
            title: 'Business Insurance',
            category: 'Assets',
            icon: <FaBriefcase />,
            description: 'Safeguard your enterprise with professional liability, property, and group health insurance.',
            detailedInfo: 'Business Insurance protects your company from risks that could threaten its existence. This includes coverage for property damage, legal liability, employee-related risks (like worker\'s compensation), and business interruption. It provides the stability needed for growth and continuity.',
            benefits: ['Risk management', 'Asset protection', 'Employee benefits (Group Health)', 'Liability coverage'],
            color: 'light'
        },
        {
            title: 'Schema',
            category: 'Investments',
            icon: <FaFileContract />,
            description: 'Navigating government and private sector investment schemes designed for wealth creation.',
            detailedInfo: 'Financial Schemas include various government-backed savings schemes like PPF, NPS, or specific corporate investment programs. These often offer attractive interest rates and tax benefits, making them ideal for risk-averse investors looking for stable growth.',
            benefits: ['Safe and secure investments', 'Government backing', 'Tax saving opportunities', 'Disciplined savings approach'],
            color: 'light'
        },
        {
            title: 'Bonds',
            category: 'Investments',
            icon: <FaBuilding />,
            description: 'Fixed-income securities providing steady returns, safety of capital, and portfolio diversification.',
            detailedInfo: 'Bonds are debt instruments where you lend money to an entity (government or corporate) for a fixed period at a fixed interest rate. They are essential for diversifying your portfolio and providing regular interest income while preserving your capital.',
            benefits: ['Regular interest income', 'Capital preservation', 'Portfolio diversification', 'Lower risk than equities'],
            color: 'light'
        },
        {
            title: 'Equity',
            category: 'Investments',
            icon: <FaChartLine />,
            description: 'Build long-term wealth through strategic stock market investments and company ownership.',
            detailedInfo: 'Equity involves buying shares of companies. While higher in risk, it has historically provided the best returns for long-term wealth creation. It allows you to participate in the growth story of successful businesses and beat inflation over time.',
            benefits: ['High long-term returns', 'Dividend income', 'Ownership in companies', 'Inflation-beating growth'],
            color: 'light'
        }
    ];


    const filteredInsurance = activeFilter === 'All'
        ? insuranceTypes
        : insuranceTypes.filter(item => item.category === activeFilter);

    return (
        <div className="insurance-page">
            {/* Trusted Partners Section */}
            <section className="trusted-partners-section">
                <div className="container">
                    <h3 className="section-title text-center mb-4">Our Trusted Insurance Partners</h3>
                    <div className="partners-grid">
                        <div className="partner-logo">LIC of India</div>
                        <div className="partner-logo">HDFC Life</div>
                        <div className="partner-logo">SBI Life</div>
                        <div className="partner-logo">ICICI Pru</div>
                        <div className="partner-logo">Star Health</div>
                        <div className="partner-logo">Max Life</div>
                    </div>
                </div>
            </section>

            {/* Filter Tabs */}
            <section className="filter-section">
                <div className="container">
                    {diagnosisReport && recommendedTypes.length > 0 && (
                        <div className="personalized-banner">
                            <div className="banner-icon"><FaStar /></div>
                            <div className="banner-text">
                                <h4>Personalized Recommendations for you, {diagnosisReport.personalInfo.name}</h4>
                                <p>Based on your recent financial diagnosis, we've highlighted the insurance and investment areas that need your attention.</p>
                            </div>
                        </div>
                    )}

                    <div className="filter-tabs">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                className={`filter-tab ${activeFilter === cat ? 'active' : ''}`}
                                onClick={() => setActiveFilter(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Insurance Grid */}
            <section className="audience-groups-section">
                <div className="container">
                    <div className="audience-groups-grid">
                        {filteredInsurance.map((item, index) => {
                            const isRecommended = recommendedTypes.includes(item.title);
                            return (
                                <div
                                    key={index}
                                    className={`audience-group-box ${item.color} ${isRecommended ? 'recommended' : ''}`}
                                    style={{ animationDelay: `${index * 0.1}s`, cursor: 'pointer' }}
                                    onClick={() => setSelectedInsurance(item)}
                                >
                                    {isRecommended && (
                                        <div className="recommend-badge">
                                            <FaStar /> RECOMMENDED
                                        </div>
                                    )}
                                    <div className="insurance-icon-box">
                                        {item.icon}
                                    </div>
                                    <span className="cat-tag">{item.category}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                    <span className="learn-more-text">LEARN MORE →</span>
                                </div>
                            );
                        })}
                    </div>
                    {filteredInsurance.length === 0 && (
                        <p className="no-results">No insurance types found in this category.</p>
                    )}
                </div>
            </section>

            {/* Modal for Details */}
            {selectedInsurance && (
                <div className="insurance-modal-overlay" onClick={() => setSelectedInsurance(null)}>
                    <div className="insurance-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setSelectedInsurance(null)}>&times;</button>
                        <span className="modal-subtitle">{selectedInsurance.category.toUpperCase()} OVERVIEW</span>
                        <div className="modal-header-with-icon">
                            <div className="modal-icon">{selectedInsurance.icon}</div>
                            <h2>{selectedInsurance.title}</h2>
                        </div>
                        <p className="modal-detailed-info">{selectedInsurance.detailedInfo}</p>

                        <div className="modal-benefits-section">
                            <h3>Key Benefits</h3>
                            <ul>
                                {selectedInsurance.benefits.map((benefit, i) => (
                                    <li key={i}>{benefit}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="modal-action">
                            <button className="cta-button primary" onClick={() => {
                                setContactModalOpen(true);
                            }}>Inquire Now</button>
                            <button className="cta-button secondary" onClick={() => setSelectedInsurance(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Form Modal */}
            {isContactModalOpen && (
                <div className="insurance-modal-overlay contact-modal-overlay" onClick={() => setContactModalOpen(false)}>
                    <div className="insurance-modal-content contact-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setContactModalOpen(false)}>&times;</button>
                        <h2>Request a Call Back</h2>
                        <p style={{ color: 'var(--color-gray)' }}>Leave your details below and our insurance expert will get in touch with you.</p>

                        <form className="contact-form" onSubmit={handleContactSubmit}>
                            <div className="form-group">
                                <label>Full Name</label>
                                <input type="text" required placeholder="Enter your full name"
                                    value={contactForm.name}
                                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input type="tel" required placeholder="Enter your phone number"
                                    value={contactForm.phone}
                                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Email Address</label>
                                <input type="email" placeholder="Enter your email address"
                                    value={contactForm.email}
                                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                />
                            </div>
                            <button
                                type="submit"
                                className="cta-button primary w-100 mt-4"
                                style={{ justifyContent: 'center', width: '100%' }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Submit Request'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Expert Consultation Section */}
            <section className="expert-consultation">
                <div className="container">
                    <div className="consultation-card shadow-lg">
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <h2>Confused about which one to choose?</h2>
                                <p>Our financial experts are here to guide you through every step, ensuring you get the best coverage at the right price.</p>
                            </div>
                            <div className="col-lg-4 text-lg-end">
                                <a href="tel:+919876543210" className="cta-button primary-glow">
                                    <FaPhoneAlt /> Book a Call
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* Bottom Message */}
            <section className="serve-message-section">
                <div className="container">
                    <div className="serve-message-card">
                        <p>
                            <strong>A secure future starts with a single step today.</strong> Yathartha is committed to being your partner in every phase of your financial journey.
                        </p>
                    </div>
                </div>
            </section>

            <style aria-hidden="true">{`
                .insurance-page .hero {
                    height: 50vh;
                    background-attachment: scroll;
                }
                .insurance-page .hero-overlay {
                    background: linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.3));
                }
                
                /* Trusted Partners */
                .trusted-partners-section {
                    padding: 3rem 0;
                    margin-top: 1rem;
                }
                .trusted-partners-section .section-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: var(--color-gray-dark);
                }
                .partners-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
                    gap: 1.5rem;
                    align-items: center;
                    justify-items: center;
                    margin-top: 2rem;
                }
                .partner-logo {
                    background: white;
                    padding: 1.2rem 1rem;
                    border-radius: 12px;
                    border: 1px solid #efefef;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.02);
                    font-weight: 700;
                    font-size: 0.9rem;
                    color: #555;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    transition: all 0.3s ease;
                    width: 100%;
                    text-align: center;
                }
                .partner-logo:hover {
                    color: var(--color-accent);
                    border-color: rgba(var(--color-accent-rgb), 0.3);
                    box-shadow: 0 6px 15px rgba(0,0,0,0.06);
                    transform: translateY(-3px);
                }

                .filter-section {
                    padding: 2rem 0;
                    margin-bottom: 2rem;
                }
                .filter-tabs {
                    display: flex;
                    justify-content: center;
                    gap: 1rem;
                    flex-wrap: wrap;
                }
                .filter-tab {
                    padding: 0.75rem 1.5rem;
                    border: 1px solid #ddd;
                    background: white;
                    border-radius: 50px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    color: var(--color-gray-dark);
                }
                .filter-tab:hover {
                    border-color: var(--color-accent);
                    color: var(--color-accent);
                }
                .filter-tab.active {
                    background: var(--color-accent);
                    color: white;
                    border-color: var(--color-accent);
                    box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
                }

                /* Personalized Recommendations Style */
                .personalized-banner {
                    background: linear-gradient(135deg, #069668 0%, #057a55 100%);
                    color: white;
                    padding: 2rem;
                    border-radius: 20px;
                    margin-bottom: 3rem;
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    box-shadow: 0 10px 30px rgba(6, 150, 104, 0.2);
                    animation: slideDown 0.6s ease-out;
                }

                .banner-icon {
                    font-size: 2.5rem;
                    color: #fbbf24;
                    filter: drop-shadow(0 0 10px rgba(251, 191, 36, 0.4));
                    animation: pulse 2s infinite;
                }

                .banner-text h4 {
                    color: white;
                    margin-bottom: 0.5rem;
                    font-size: 1.4rem;
                    font-weight: 700;
                }

                .banner-text p {
                    margin-bottom: 0;
                    opacity: 0.9;
                    font-size: 1.1rem;
                    line-height: 1.5;
                }

                .audience-group-box.recommended {
                    border: 2px solid #fbbf24 !important;
                    background: #fffdf5;
                    transform: scale(1.02);
                }

                .recommend-badge {
                    position: absolute;
                    top: 15px;
                    right: 15px;
                    background: #fbbf24;
                    color: #000;
                    padding: 4px 12px;
                    border-radius: 50px;
                    font-size: 0.7rem;
                    font-weight: 800;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    box-shadow: 0 4px 10px rgba(251, 191, 36, 0.3);
                }

                @keyframes slideDown {
                    from { transform: translateY(-30px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }

                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                    100% { transform: scale(1); }
                }

                .insurance-icon-box {
                    font-size: 2.5rem;
                    color: var(--color-accent);
                    margin-bottom: 1.5rem;
                    background: rgba(var(--color-accent-rgb), 0.1);
                    width: 60px;
                    height: 60px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    transition: transform 0.3s ease;
                }
                .audience-group-box:hover .insurance-icon-box {
                    background: var(--color-accent);
                    color: white;
                }

                .cat-tag {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: var(--color-accent);
                    font-weight: 700;
                    display: block;
                    margin-bottom: 0.5rem;
                }

                .learn-more-text {
                    display: block;
                    margin-top: 1.5rem;
                    font-size: 0.875rem;
                    font-weight: 700;
                    color: var(--color-accent);
                    letter-spacing: 1px;
                }

                /* Modal Enhancements */
                .modal-header-with-icon {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    margin-bottom: 1.5rem;
                }
                .modal-icon {
                    font-size: 3rem;
                    color: var(--color-accent);
                }
                .modal-action {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                    border-top: 1px solid #eee;
                    padding-top: 2rem;
                }
                .cta-button {
                    padding: 0.8rem 2rem;
                    border-radius: 50px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    text-decoration: none;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    border: none;
                }
                .insurance-modal-content .cta-button.primary {
                    background: var(--color-accent) !important;
                    color: white !important;
                    border: none;
                }
                .insurance-modal-content .cta-button.secondary {
                    background: #f0f0f0;
                    color: #333333 !important;
                    border: 1px solid #ddd;
                }
                .insurance-modal-content .cta-button.secondary:hover {
                    background: #e0e0e0;
                    color: #000000 !important;
                }
                .cta-button.primary-glow {
                    background: var(--color-accent);
                    color: white;
                    box-shadow: 0 0 20px rgba(var(--color-accent-rgb), 0.4);
                }

                /* Expert Section */
                .expert-consultation {
                    padding: 4rem 0;
                }
                .consultation-card {
                    background: white;
                    padding: 3rem;
                    border-radius: var(--radius-xl);
                    border-left: 5px solid var(--color-accent);
                }
                .consultation-card h2 {
                    margin-bottom: 1rem;
                    font-weight: 800;
                }
                .consultation-card p {
                    font-size: 1.1rem;
                    color: var(--color-gray);
                    margin-bottom: 0;
                }


                .bg-light-gray { background: #f8f9fa; }
                .shadow-lg { box-shadow: 0 1rem 3rem rgba(0,0,0,0.1) !important; }
                .shadow-sm { box-shadow: 0 .125rem .25rem rgba(0,0,0,0.075) !important; }

                .insurance-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0,0,0,0.85);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2000;
                    padding: 2rem;
                    backdrop-filter: blur(8px);
                }
                .insurance-modal-content {
                    background: white;
                    padding: 3.5rem;
                    border-radius: var(--radius-xl, 20px);
                    max-width: 850px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    position: relative;
                    animation: modalIn 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
                }
                
                /* Contact Modal Specific Styles */
                .contact-modal-content {
                    max-width: 500px;
                    padding: 2.5rem;
                }
                .contact-modal-content h2 {
                    margin-bottom: 0.5rem;
                }
                .contact-form {
                    margin-top: 2rem;
                    text-align: left;
                }
                .contact-form .form-group {
                    margin-bottom: 1.25rem;
                }
                .contact-form label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    font-size: 0.9rem;
                    color: var(--color-gray-dark);
                }
                .contact-form input {
                    width: 100%;
                    padding: 0.8rem 1rem;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 1rem;
                    transition: all 0.3s;
                }
                .contact-form input:focus {
                    outline: none;
                    border-color: var(--color-accent);
                    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb, 0,123,255), 0.1);
                }

                @keyframes modalIn {
                    from { transform: scale(0.9) translateY(40px); opacity: 0; }
                    to { transform: scale(1) translateY(0); opacity: 1; }
                }
                .modal-close {
                    position: absolute;
                    top: 1.5rem;
                    right: 1.5rem;
                    background: none;
                    border: none;
                    font-size: 2.5rem;
                    cursor: pointer;
                    color: var(--color-gray);
                    line-height: 1;
                }
                .modal-subtitle {
                    color: var(--color-accent);
                    font-weight: 700;
                    letter-spacing: 2px;
                    font-size: 0.8rem;
                    display: block;
                    margin-bottom: 0.5rem;
                }
                .modal-detailed-info {
                    font-size: 1.1rem;
                    line-height: 1.8;
                    color: var(--color-gray-dark);
                    margin-bottom: 2.5rem;
                }
                .modal-benefits-section h3 {
                    font-size: 1.3rem;
                    margin-bottom: 1.5rem;
                    font-weight: 700;
                }
                .modal-benefits-section ul {
                    list-style: none;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.25rem;
                    margin-bottom: 2.5rem;
                    padding: 0;
                }
                .modal-benefits-section li {
                    padding-left: 2rem;
                    position: relative;
                    color: var(--color-gray-dark);
                    font-size: 1rem;
                }
                .modal-benefits-section li::before {
                    content: '✓';
                    position: absolute;
                    left: 0;
                    color: var(--color-accent);
                    font-weight: 900;
                    background: rgba(var(--color-accent-rgb), 0.1);
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.75rem;
                }

                @media (max-width: 991px) {
                    .consultation-card { text-align: center; }
                    .consultation-card div[class*="col-"]:first-child { margin-bottom: 2rem; }
                }

                @media (max-width: 768px) {
                    .insurance-page {
                        padding-top: 20px;
                    }
                    .insurance-page .hero {
                        height: auto;
                        min-height: 480px;
                        padding-top: 100px;
                    }
                    .personalized-banner {
                        flex-direction: column;
                        text-align: center;
                        padding: 1.5rem;
                        gap: 1rem;
                    }
                    .banner-icon { font-size: 2rem; }
                    .banner-text h4 { font-size: 1.2rem; }
                    .banner-text p { font-size: 1rem; }

                    .partners-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 1rem;
                    }

                    .filter-tabs {
                        gap: 0.5rem;
                    }
                    .filter-tab {
                        padding: 0.6rem 1rem;
                        font-size: 0.85rem;
                    }

                    .audience-groups-grid {
                        grid-template-columns: 1fr !important;
                        gap: 1.5rem;
                    }

                    .insurance-modal-content {
                        padding: 2.5rem 1.5rem;
                    }
                    .modal-benefits-section ul {
                        grid-template-columns: 1fr;
                        gap: 1rem;
                    }
                    .section-header h2 { font-size: 2rem; }
                    .modal-header-with-icon { flex-direction: column; text-align: center; gap: 1rem; }
                    
                    .expert-consultation { padding: 2rem 0; }
                    .consultation-card { padding: 2rem 1.5rem; }
                }
            `}</style>
        </div>
    );
};

export default Insurance;

